import type { AgentInputItem } from '@openai/agents';
import type { BoardUser, NCCluster, NCNode } from '../../commonTypes';
import type {
	IAgentMemory,
	IAgentMessage,
	IAgentPlan,
	IAgentToolCall,
	IAgentToolResult,
} from '../../models/agentMemory.model';
import type { Board } from '../../models/board.model';
import type { TurnIntent } from './intentRouter';

export type FlowyToolName =
	| 'addConcepts'
	| 'spark'
	| 'empathise'
	| 'brainstorm'
	| 'persona'
	| 'search'
	| 'pdfSearch'
	| 'boardState';

export type AgentNodeReference = {
	uid: string;
	label: string;
	colour?: string;
	nodeColour?: string;
	imageUrl?: string;
};

export type BoardSnapshot = {
	boardId: string;
	title: string;
	engine: string;
	phase: 'inspire' | 'ideate' | 'moodboard';
	goal: string;
	summary: string;
	vision: string;
	hasPdf: boolean;
	selectedFileName: string | null;
	nodeCount: number;
	recentNodes: Array<{
		uid: string;
		label: string;
		colour: string;
	}>;
	personas: string[];
	topClusters: Array<{
		group: string;
		values: string[];
	}>;
	inspireProgress?: import('../orchestrator/inspireProgress').InspireProgress;
};

export type FlowyAgentContext = {
	boardId: string;
	userId: string;
	userToken: string;
	board: Omit<Board, 'addUser'>;
	boardUser: BoardUser;
	sessionId: string;
	snapshot: BoardSnapshot;
	memory: IAgentMemory;
	externalToolSummary?: string;
	intent?: TurnIntent;
	canEditPlan?: boolean;
};

export type FlowyRunInput = {
	boardId: string;
	userId: string;
	userToken: string;
	board: Omit<Board, 'addUser'>;
	boardUser: BoardUser;
	sessionId: string;
	message: string;
	memory: IAgentMemory;
	snapshot: BoardSnapshot;
	intent?: TurnIntent;
	canEditPlan?: boolean;
};

export type FlowyRunResult = {
	message: string;
	goal: string | null;
	plan: IAgentPlan | null;
	toolCalls: IAgentToolCall[];
	toolResults: IAgentToolResult[];
	nodeReferences: AgentNodeReference[];
	history: AgentInputItem[];
};

export type AgentHistoryResponse = Pick<
	IAgentMemory,
	'sessionId' | 'goal' | 'plan' | 'planAccepted' | 'summary' | 'messages' | 'updatedAt' | 'createdAt'
>;

export const isNodeLabel = (value: NCNode['label']): value is string =>
	typeof value === 'string' && value.trim().length > 0;

export const compactNodeLabel = (node: NCNode) => (isNodeLabel(node.label) ? node.label.trim() : '');

export const compactCluster = (cluster: NCCluster) => ({
	group: cluster.group,
	values: cluster.values.slice(0, 6),
});

export const serializeToolCalls = (toolCalls: IAgentToolCall[]): string =>
	toolCalls
		.map((toolCall) => `${toolCall.name}(${toolCall.arguments})`)
		.join(', ');

export const latestAssistantMessage = (messages: IAgentMessage[]) =>
	[...messages].reverse().find((message) => message.role === 'assistant');
